import { useCallback, type Dispatch, type SetStateAction } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'
import api from '@/services/api'
import type { BdrLead, BdrCloser } from '@/types'
import { mapBdrLeadValidationErrors } from '@/hooks/bdrPanel/bdrLeadMutationErrors'
import { extractApiError } from '@/utils/extractApiError'
import { getFriendlyError } from '@/utils/getFriendlyError'
import { MIN_DORES_LENGTH, emptyRegTentativa } from '@/utils/bdrPanelUtils'

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
import { isBlockingDealEtapa } from '@/utils/leadRegistrationValidation'

type NewLeadForm = Record<string, unknown>
type RegTentativaForm = ReturnType<typeof emptyRegTentativa>

export type BdrLeadMutationsParams = {
  currentBdrId: string
  viewTab: string
  closers: BdrCloser[]
  selectedLead: BdrLead | null
  newLead: NewLeadForm
  regTentativa: RegTentativaForm
  setSelectedLead: Dispatch<SetStateAction<BdrLead | null>>
  setIsEditing: (v: boolean) => void
  setBackendErrors: (errors: Record<string, string>) => void
  setShowRegTentativa: (v: boolean) => void
  setSavingTentativa: (v: boolean) => void
  setRegTentativa: (v: RegTentativaForm) => void
  setLeadsList: Dispatch<SetStateAction<BdrLead[]>>
  setQueueLeads: Dispatch<SetStateAction<BdrLead[]>>
  handleCloseModal: () => void
  fetchTodayQueue: () => Promise<void>
  fetchAllLeads: (p?: number, append?: boolean) => Promise<void>
  fetchBankLeads: (p?: number) => Promise<void>
}

export function useBdrPanelLeadMutations(params: BdrLeadMutationsParams) {
  const navigate = useNavigate()

  const handleAddLead = useCallback(async () => {
    params.setBackendErrors({})
    const lead = params.newLead as { empresa?: string; contato?: string; telefone?: string; empresaId?: string }

    if (!lead.empresa?.trim()) {
      params.setBackendErrors({ empresa: 'Empresa é obrigatória' })
      toast.error('Preencha o campo empresa')
      return
    }
    if (!lead.contato?.trim()) {
      params.setBackendErrors({ contato: 'Contato é obrigatório' })
      toast.error('Preencha o campo contato')
      return
    }
    if (!lead.telefone?.trim()) {
      params.setBackendErrors({ telefone: 'Telefone é obrigatório' })
      toast.error('Preencha o campo telefone')
      return
    }

    try {
      if (lead.empresaId) {
        const { data: existingEmpresa } = await api.get(`/empresas/${lead.empresaId}`)
        const deals = existingEmpresa?.deals || existingEmpresa?.data?.deals || []
        const leads = existingEmpresa?.leads || existingEmpresa?.data?.leads || []

        const blockingDeal = deals.find((d: { etapa?: string }) => isBlockingDealEtapa(d.etapa))
        if (blockingDeal) {
          toast.error('Acesso negado: Esta empresa já possui um deal em negociação ativa.')
          params.setBackendErrors({ empresa: 'Empresa já está em negociação ativa' })
          return
        }

        const blockingLead = leads.find((l: { status?: string }) => l.status !== 'DESCARTADO')
        if (blockingLead) {
          toast.error(
            `Acesso negado: Esta empresa já possui um Lead em andamento (Status: ${blockingLead.status}). Use o lead existente ou aguarde o descarte.`,
          )
          params.setBackendErrors({ empresa: 'Empresa com lead ativo' })
          return
        }
      }

      await api.post('/bdr/leads', { ...params.newLead, bdrId: params.currentBdrId })
      toast.success('Lead criado com sucesso')
      params.handleCloseModal()
      await params.fetchTodayQueue()
      if (params.viewTab === 'all') await params.fetchAllLeads(1)
      else if (params.viewTab === 'bank') await params.fetchBankLeads(1)
    } catch (err: unknown) {
      const mapped = mapBdrLeadValidationErrors(err)
      if (mapped) {
        params.setBackendErrors(mapped)
        toast.error('Corrija os campos destacados')
        return
      }
      const axiosErr = err as {
        response?: { status?: number; data?: { error?: { message?: string }; message?: string } }
      }
      const status = axiosErr.response?.status
      const respData = axiosErr.response?.data
      if (status === 409) {
        toast.error(respData?.error?.message ?? respData?.message ?? 'Lead duplicado encontrado')
      } else {
        toast.error(getFriendlyError(err, respData?.error?.message ?? respData?.message ?? 'Erro ao criar lead'))
      }
    }
  }, [params])

  const handleUpdateLead = useCallback(async () => {
    if (!params.selectedLead) return
    params.setBackendErrors({})
    try {
      const { data } = await api.patch(`/bdr/leads/${params.selectedLead.id}`, params.newLead)
      toast.success('Lead atualizado')
      params.setSelectedLead(data.data)
      params.setIsEditing(false)
      if (params.viewTab === 'queue') params.fetchTodayQueue()
      else if (params.viewTab === 'all') params.fetchAllLeads(1)
    } catch (err: unknown) {
      const mapped = mapBdrLeadValidationErrors(err)
      if (mapped) {
        params.setBackendErrors(mapped)
        toast.error('Corrija os campos destacados')
      } else {
        const axiosErr = err as {
          response?: { data?: { error?: { message?: string }; message?: string } }
        }
        const resp = axiosErr.response?.data
        toast.error(getFriendlyError(err, resp?.error?.message ?? resp?.message ?? 'Erro ao atualizar lead'))
      }
    }
  }, [params])

  const handleRegTentativa = useCallback(async () => {
    if (!params.selectedLead) return
    if (!params.regTentativa.canal || !params.regTentativa.resultado) {
      toast.error('Preencha canal e resultado')
      return
    }
    if (params.regTentativa.resultado === 'Reunião Agendada') {
      const email = params.regTentativa.email?.trim() ?? ''
      if (!email) {
        toast.error('Informe o e-mail para converter o lead em reunião')
        return
      }
      if (!EMAIL_PATTERN.test(email)) {
        toast.error('Informe um e-mail válido para o contato')
        return
      }
      if (!params.regTentativa.closerId) {
        toast.error('Selecione o Closer para agendar a reunião')
        return
      }
      if (!params.regTentativa.dataReuniao || !params.regTentativa.horaReuniao) {
        toast.error('Informe a data e hora da reunião')
        return
      }
      const dores = params.regTentativa.dores?.trim() ?? ''
      if (dores.length < MIN_DORES_LENGTH) {
        toast.error(`Descreva as dores/problema do lead (mínimo ${MIN_DORES_LENGTH} caracteres)`)
        return
      }
      const closerName = params.closers.find((c) => c.id === params.regTentativa.closerId)?.name ?? 'Closer'
      if (
        !window.confirm(
          `Confirmar handoff para ${closerName} em ${params.regTentativa.dataReuniao} às ${params.regTentativa.horaReuniao}?`,
        )
      ) {
        return
      }
    }
    params.setSavingTentativa(true)
    try {
      const body: Record<string, string> = {
        canal: params.regTentativa.canal,
        resultado: params.regTentativa.resultado,
        obs: params.regTentativa.obs,
      }
      if (params.regTentativa.resultado === 'Reunião Agendada') {
        body.email = params.regTentativa.email.trim()
        body.closerId = params.regTentativa.closerId
        body.dataReuniao = `${params.regTentativa.dataReuniao}T${params.regTentativa.horaReuniao}:00`
        body.dores = params.regTentativa.dores.trim()
      }
      const postRes = await api.post(`/bdr/leads/${params.selectedLead.id}/tentativa`, body)
      const postedLead = postRes.data?.data?.lead as BdrLead | undefined
      toast.success(
        params.regTentativa.resultado === 'Reunião Agendada'
          ? 'Lead convertido! Handoff enviado ao Closer.'
          : 'Tentativa registrada',
      )
      params.setShowRegTentativa(false)
      params.setRegTentativa(emptyRegTentativa())
      if (postedLead) {
        params.setSelectedLead((prev) =>
          prev ? { ...prev, ...postedLead, pendencias: prev.pendencias ?? postedLead.pendencias } : postedLead,
        )
        params.setQueueLeads((prev) =>
          prev.map((l) => (l.id === params.selectedLead!.id ? { ...l, ...postedLead } : l)),
        )
        params.setLeadsList((prev) =>
          prev.map((l) => (l.id === params.selectedLead!.id ? { ...l, ...postedLead } : l)),
        )
      } else {
        const { data } = await api.get(`/bdr/leads/${params.selectedLead.id}`)
        params.setSelectedLead(data.data)
      }
      if (params.viewTab === 'queue') params.fetchTodayQueue()
      if (params.regTentativa.resultado === 'Reunião Agendada') {
        setTimeout(() => navigate('/deals'), 1500)
      }
    } catch (err: unknown) {
      const extracted = extractApiError(err, 'Erro ao registrar tentativa')
      toast.error(getFriendlyError(err, extracted.message))
    } finally {
      params.setSavingTentativa(false)
    }
  }, [params, navigate])

  const handleDeleteLead = useCallback(
    async (leadId: string) => {
      if (!window.confirm('Tem certeza que deseja excluir este lead?')) return
      try {
        await api.delete(`/bdr/leads/${leadId}`)
        toast.success('Lead excluído')
        params.setLeadsList((prev) => prev.filter((l) => l.id !== leadId))
        params.setQueueLeads((prev) => prev.filter((l) => l.id !== leadId))
        if (params.selectedLead?.id === leadId) params.handleCloseModal()
      } catch (err: unknown) {
        toast.error(getFriendlyError(err, 'Erro ao excluir lead'))
      }
    },
    [params],
  )

  const handleRecoverLead = useCallback(
    async (leadId: string) => {
      try {
        await api.post(`/bdr/recover/${leadId}`)
        toast.success('Lead recuperado')
        if (params.viewTab === 'queue') params.fetchTodayQueue()
        else if (params.viewTab === 'all') params.fetchAllLeads(1)
      } catch (err: unknown) {
        toast.error(getFriendlyError(err, 'Erro ao recuperar lead'))
      }
    },
    [params],
  )

  const handleDiscardLead = useCallback(
    async (leadId: string) => {
      if (!window.confirm('Tem certeza que deseja descartar este lead?')) return
      try {
        await api.post(`/bdr/leads/${leadId}/discard`)
        toast.success('Lead descartado')
        params.setQueueLeads((prev) => prev.filter((l) => l.id !== leadId))
        params.setLeadsList((prev) => prev.filter((l) => l.id !== leadId))
        if (params.selectedLead?.id === leadId) params.handleCloseModal()
      } catch (err: unknown) {
        toast.error(getFriendlyError(err, 'Erro ao descartar lead'))
      }
    },
    [params],
  )

  const handleMoveToNurturing = useCallback(
    async (leadId: string) => {
      try {
        await api.post(`/bdr/leads/${leadId}/nurture`)
        toast.success('Lead movido para nutrição')
        params.setQueueLeads((prev) => prev.filter((l) => l.id !== leadId))
        params.setLeadsList((prev) => prev.filter((l) => l.id !== leadId))
        if (params.selectedLead?.id === leadId) params.handleCloseModal()
      } catch (err: unknown) {
        toast.error(getFriendlyError(err, 'Erro ao mover para nutrição'))
      }
    },
    [params],
  )

  return {
    handleAddLead,
    handleUpdateLead,
    handleRegTentativa,
    handleDeleteLead,
    handleRecoverLead,
    handleDiscardLead,
    handleMoveToNurturing,
  }
}
